import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, Receipt, Car, Utensils, Hotel, MoreHorizontal, 
  CheckCircle, XCircle, Clock, Upload, Eye, Trash2, FileText, Loader2, Download, Pencil
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPENSE_TYPES = [
  { value: "yol", label: "Yol / Ulaşım", icon: Car, color: "text-blue-600 bg-blue-100" },
  { value: "yemek", label: "Yemek", icon: Utensils, color: "text-orange-600 bg-orange-100" },
  { value: "konaklama", label: "Konaklama", icon: Hotel, color: "text-purple-600 bg-purple-100" },
  { value: "diger", label: "Diğer", icon: MoreHorizontal, color: "text-gray-600 bg-gray-100" },
];

// Helper to get date in Turkey timezone as YYYY-MM-DD
const getTurkeyDateString = (date = new Date()) => {
  const options = { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Intl.DateTimeFormat('sv-SE', options).format(date);
};

const STATUS_CONFIG = {
  pending: { label: "Beklemede", color: "text-yellow-600 bg-yellow-100", icon: Clock },
  approved: { label: "Onaylandı", color: "text-green-600 bg-green-100", icon: CheckCircle },
  rejected: { label: "Reddedildi", color: "text-red-600 bg-red-100", icon: XCircle },
};

// Rejection reason dialog component
const RejectDialog = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  
  const handleSubmit = () => {
    if (!reason.trim()) {
      toast.error("Reddetme gerekçesi zorunludur");
      return;
    }
    onConfirm(reason, notes);
    setReason("");
    setNotes("");
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            Masraf Reddetme
          </DialogTitle>
          <DialogDescription>
            Masrafı reddetmek için gerekçe belirtmeniz gerekmektedir.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="rejection_reason">Reddetme Gerekçesi *</Label>
            <Textarea
              id="rejection_reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Masrafın neden reddedildiğini açıklayın..."
              rows={3}
              className="mt-1"
              data-testid="rejection-reason-input"
            />
          </div>
          <div>
            <Label htmlFor="review_notes">Ek Notlar (Opsiyonel)</Label>
            <Input
              id="review_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Varsa ek bilgiler..."
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button 
              variant="destructive" 
              onClick={handleSubmit}
              data-testid="confirm-reject-btn"
            >
              Reddet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Component to load receipt images with authentication
const ReceiptImage = ({ url, alt, index }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await axios.get(url, {
          withCredentials: true,
          responseType: 'blob'
        });
        const objectUrl = URL.createObjectURL(response.data);
        setImageSrc(objectUrl);
        setLoading(false);
      } catch (err) {
        console.error("Error loading receipt:", err);
        setError(true);
        setLoading(false);
      }
    };
    loadImage();
    
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [url]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-100 rounded">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-100 rounded">
        <FileText className="w-8 h-8 text-gray-400" />
        <span className="ml-2 text-gray-500">Dosya yüklenemedi</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className="max-w-full max-h-96 mx-auto object-contain"
    />
  );
};

const ExpensesPage = () => {
  const { user } = useContext(AuthContext);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ by_type: {}, by_status: {}, grand_total: 0 });
  const [audits, setAudits] = useState([]);
  const [royalcertAudits, setRoyalcertAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterAuditId, setFilterAuditId] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterAuditor, setFilterAuditor] = useState("all");
  const [auditors, setAuditors] = useState([]);
  
  // Rejection dialog state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [expenseToReject, setExpenseToReject] = useState(null);
  
  // Edit mode state
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Date range filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    expense_type: "",
    amount: "",
    description: "",
    expense_date: getTurkeyDateString(),
    audit_id: ""
  });

  const isAuditor = user?.role === "auditor";
  const canReview = ["admin", "super_admin", "technical_manager", "planning_expert"].includes(user?.role);
  const canDelete = ["admin", "super_admin", "technical_manager"].includes(user?.role);

  const fetchExpenses = useCallback(async () => {
    try {
      const [expensesRes, summaryRes, auditsRes, royalcertAuditsRes, auditorsRes] = await Promise.all([
        axios.get(`${API}/expenses`, { withCredentials: true }),
        axios.get(`${API}/expenses/summary`, { withCredentials: true }),
        axios.get(`${API}/audits`, { withCredentials: true }),
        axios.get(`${API}/royalcert-audits`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/users/role/auditor`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setAudits(auditsRes.data);
      setRoyalcertAudits(royalcertAuditsRes.data);
      setAuditors(auditorsRes.data);
    } catch (error) {
      console.error("Failed to fetch expenses:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Filter expenses by audit, status, type, auditor, and date range
  const filteredExpenses = expenses.filter(e => {
    // Audit filter
    if (filterAuditId && filterAuditId !== "all" && e.audit_id !== filterAuditId) {
      return false;
    }
    // Status filter
    if (filterStatus && filterStatus !== "all" && e.status !== filterStatus) {
      return false;
    }
    // Type filter
    if (filterType && filterType !== "all" && e.expense_type !== filterType) {
      return false;
    }
    // Auditor filter
    if (filterAuditor && filterAuditor !== "all" && e.auditor_id !== filterAuditor) {
      return false;
    }
    // Date range filter
    if (startDate) {
      const expenseDate = getTurkeyDateString(new Date(e.expense_date));
      if (expenseDate < startDate) return false;
    }
    if (endDate) {
      const expenseDate = getTurkeyDateString(new Date(e.expense_date));
      if (expenseDate > endDate) return false;
    }
    return true;
  });
  
  // Calculate filtered total
  const filteredTotal = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  // Get audit name by ID
  const getAuditName = (expense) => {
    // First check if backend already provided the customer name
    if (expense.audit_customer_name) {
      return expense.audit_customer_name;
    }
    
    const auditId = expense.audit_id;
    if (!auditId) return "-";
    
    // Fallback: check local audits list
    const audit = audits.find(a => a.audit_id === auditId);
    if (audit) {
      return audit.customer_name || auditId;
    }
    
    // Check RoyalCert audits
    const rcAudit = royalcertAudits.find(a => a.audit_record_id === auditId);
    if (rcAudit) {
      return rcAudit.customer_name || auditId;
    }
    
    return auditId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.expense_type || !formData.amount || !formData.expense_date) {
      toast.error("Lütfen zorunlu alanları doldurun");
      return;
    }
    
    if (!formData.audit_id) {
      toast.error("Lütfen bir denetim seçin");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        expense_date: new Date(formData.expense_date).toISOString()
      };
      
      if (editingExpense) {
        // Update existing expense
        await axios.put(`${API}/expenses/${editingExpense.expense_id}`, payload, { withCredentials: true });
        toast.success(editingExpense.status === "rejected" 
          ? "Masraf güncellendi ve yeniden gönderildi" 
          : "Masraf güncellendi"
        );
      } else {
        // Create new expense
        await axios.post(`${API}/expenses`, payload, { withCredentials: true });
        toast.success("Masraf eklendi");
      }
      
      setShowAddDialog(false);
      setEditingExpense(null);
      setFormData({
        expense_type: "",
        amount: "",
        description: "",
        expense_date: getTurkeyDateString(),
        audit_id: ""
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };
  
  // Reset form when dialog closes
  const handleDialogClose = (open) => {
    if (!open) {
      setEditingExpense(null);
      setFormData({
        expense_type: "",
        amount: "",
        description: "",
        expense_date: getTurkeyDateString(),
        audit_id: ""
      });
    }
    setShowAddDialog(open);
  };

  const handleFileUpload = async (e) => {
    if (!selectedExpense || !e.target.files?.[0]) return;
    
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(true);
    try {
      await axios.post(
        `${API}/expenses/${selectedExpense.expense_id}/receipt`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Fiş yüklendi");
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fiş yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const handleReview = async (expenseId, status, rejectionReason = null, reviewNotes = null) => {
    try {
      const payload = { status };
      if (status === "rejected") {
        payload.rejection_reason = rejectionReason;
        payload.review_notes = reviewNotes;
      }
      
      await axios.put(`${API}/expenses/${expenseId}/review`, payload, { withCredentials: true });
      
      toast.success(status === "approved" ? "Masraf onaylandı" : "Masraf reddedildi");
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };
  
  // Open reject dialog
  const openRejectDialog = (expense) => {
    setExpenseToReject(expense);
    setShowRejectDialog(true);
  };
  
  // Confirm rejection with reason
  const confirmReject = (reason, notes) => {
    if (expenseToReject) {
      handleReview(expenseToReject.expense_id, "rejected", reason, notes);
    }
    setExpenseToReject(null);
  };
  
  // Edit expense (for resubmission)
  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expense_type: expense.expense_type,
      amount: expense.amount.toString(),
      description: expense.description || "",
      expense_date: expense.expense_date.split("T")[0],
      audit_id: expense.audit_id || ""
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm("Bu masrafı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/expenses/${expenseId}`, { withCredentials: true });
      toast.success("Masraf silindi");
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Masraf silinemedi");
    }
  };

  const getTypeConfig = (type) => EXPENSE_TYPES.find(t => t.value === type) || EXPENSE_TYPES[3];
  const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('tr-TR');

  // Export expenses to Excel
  const handleExportExcel = () => {
    if (filteredExpenses.length === 0) {
      toast.error("Export edilecek masraf yok");
      return;
    }
    
    // Create CSV content
    const headers = ["Tarih", "Denetim", "Denetçi", "Masraf Türü", "Açıklama", "Tutar (TRY)", "Durum", "Fiş Sayısı"];
    const typeLabels = { yol: "Yol/Ulaşım", yemek: "Yemek", konaklama: "Konaklama", diger: "Diğer" };
    const statusLabels = { pending: "Beklemede", approved: "Onaylandı", rejected: "Reddedildi" };
    
    const rows = filteredExpenses.map(exp => [
      formatDate(exp.expense_date),
      getAuditName(exp),
      exp.auditor_name,
      typeLabels[exp.expense_type] || exp.expense_type,
      exp.description || "",
      exp.amount.toFixed(2),
      statusLabels[exp.status] || exp.status,
      exp.receipt_urls?.length || 0
    ]);
    
    // Add date range info if filtered
    if (startDate || endDate) {
      rows.unshift([]);
      rows.unshift([`Tarih Aralığı: ${startDate || "Başlangıç yok"} - ${endDate || "Bitiş yok"}`]);
    }
    
    // Add summary row
    rows.push([]);
    rows.push(["", "", "", "", "TOPLAM:", filteredTotal.toFixed(2), "", ""]);
    
    // Convert to CSV with BOM for Turkish characters
    const BOM = "\uFEFF";
    const csvContent = BOM + [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    
    // Download with date range in filename
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const dateRangeStr = startDate && endDate ? `_${startDate}_${endDate}` : "";
    link.download = `Masraflar${dateRangeStr}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Masraflar Excel'e aktarıldı");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A4D2E]">Masraflar</h1>
          <p className="text-gray-500 text-sm">
            {isAuditor ? "Denetim masraflarınızı yönetin" : "Denetçi masraflarını görüntüleyin ve onaylayın"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canReview && filteredExpenses.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleExportExcel}
              data-testid="export-expenses-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel'e Aktar ({filteredExpenses.length})
            </Button>
          )}
          {isAuditor && (
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
              data-testid="add-expense-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Masraf Ekle
            </Button>
          )}
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="border-[#E8F3EE]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-sm text-gray-500">Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-gray-500">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            {(startDate || endDate) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setStartDate(""); setEndDate(""); }}
                className="text-gray-500"
              >
                Temizle
              </Button>
            )}
            <div className="ml-auto text-right">
              <p className="text-sm text-gray-500">Filtrelenmiş Toplam</p>
              <p className="text-lg font-bold text-[#1A4D2E]">{formatCurrency(filteredTotal)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E8F3EE]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#E8F3EE]">
                <Receipt className="w-5 h-5 text-[#1A4D2E]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam</p>
                <p className="text-lg font-bold text-[#1A4D2E]">{formatCurrency(summary.grand_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {EXPENSE_TYPES.slice(0, 3).map(type => {
          const typeData = summary.by_type[type.value] || { total: 0, count: 0 };
          const TypeIcon = type.icon;
          return (
            <Card key={type.value} className="border-[#E8F3EE]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${type.color.split(' ')[1]}`}>
                    <TypeIcon className={`w-5 h-5 ${type.color.split(' ')[0]}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{type.label}</p>
                    <p className="text-lg font-bold">{formatCurrency(typeData.total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Expenses Table */}
      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <CardTitle className="text-lg mb-4">Masraf Listesi</CardTitle>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Audit Filter */}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Denetim</Label>
              <Select value={filterAuditId} onValueChange={setFilterAuditId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {audits.map(audit => (
                    <SelectItem key={audit.audit_id} value={audit.audit_id}>
                      {audit.customer_name?.substring(0, 20)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Durum</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="pending">Beklemede</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="rejected">Reddedildi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Type Filter */}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Masraf Türü</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {EXPENSE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Auditor Filter - Only show for admins */}
            {!isAuditor && auditors.length > 0 && (
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Denetçi</Label>
              <Select value={filterAuditor} onValueChange={setFilterAuditor}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {auditors.map(auditor => (
                    <SelectItem key={auditor.user_id} value={auditor.user_id}>
                      {auditor.full_name || auditor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            
            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setFilterAuditId("all");
                  setFilterStatus("all");
                  setFilterType("all");
                  setFilterAuditor("all");
                  setStartDate("");
                  setEndDate("");
                }}
                className="w-full"
              >
                Filtreleri Temizle
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{filterAuditId ? "Bu denetime ait masraf yok" : "Henüz masraf kaydı yok"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Denetim</TableHead>
                    <TableHead>Tür</TableHead>
                    {!isAuditor && <TableHead>Denetçi</TableHead>}
                    <TableHead>Açıklama</TableHead>
                    <TableHead className="text-right">Tutar</TableHead>
                    <TableHead>Fiş</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => {
                    const typeConfig = getTypeConfig(expense.expense_type);
                    const statusConfig = STATUS_CONFIG[expense.status] || STATUS_CONFIG.pending;
                    const TypeIcon = typeConfig.icon;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <TableRow key={expense.expense_id}>
                        <TableCell className="font-medium">
                          {formatDate(expense.expense_date)}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="text-[#1A4D2E] font-medium">
                            {getAuditName(expense)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${typeConfig.color.split(' ')[1]}`}>
                              <TypeIcon className={`w-4 h-4 ${typeConfig.color.split(' ')[0]}`} />
                            </div>
                            <span className="text-sm">{typeConfig.label}</span>
                          </div>
                        </TableCell>
                        {!isAuditor && (
                          <TableCell className="text-sm text-gray-600">
                            {expense.auditor_name}
                          </TableCell>
                        )}
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                          {expense.description || "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(expense.amount)}
                        </TableCell>
                        <TableCell>
                          {expense.receipt_urls?.length > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedExpense(expense);
                                setShowReceiptDialog(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {expense.receipt_urls.length}
                            </Button>
                          ) : isAuditor && expense.status === "pending" ? (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  setSelectedExpense(expense);
                                  handleFileUpload(e);
                                }}
                              />
                              <span className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1A4D2E]">
                                <Upload className="w-4 h-4" />
                                Ekle
                              </span>
                            </label>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div 
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                            title={expense.status === "rejected" && expense.rejection_reason 
                              ? `Gerekçe: ${expense.rejection_reason}` 
                              : statusConfig.label}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </div>
                          {expense.status === "rejected" && expense.rejection_reason && (
                            <div className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={expense.rejection_reason}>
                              {expense.rejection_reason}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Review buttons for pending expenses */}
                            {canReview && expense.status === "pending" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReview(expense.expense_id, "approved")}
                                  className="text-green-600 hover:text-green-800 hover:bg-green-50"
                                  title="Onayla"
                                  data-testid={`approve-expense-${expense.expense_id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openRejectDialog(expense)}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  title="Reddet"
                                  data-testid={`reject-expense-${expense.expense_id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {/* Edit button for owner's rejected or pending expenses */}
                            {expense.auditor_id === user?.user_id && 
                              (expense.status === "pending" || expense.status === "rejected") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(expense)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                title={expense.status === "rejected" ? "Düzenle ve Yeniden Gönder" : "Düzenle"}
                                data-testid={`edit-expense-${expense.expense_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Delete button */}
                            {(isAuditor && expense.status === "pending") || canDelete ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(expense.expense_id)}
                                className="text-gray-500 hover:text-red-600"
                                title="Sil"
                                data-testid={`delete-expense-${expense.expense_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingExpense 
                ? (editingExpense.status === "rejected" ? "Masrafı Düzenle ve Yeniden Gönder" : "Masraf Düzenle")
                : "Yeni Masraf Ekle"
              }
            </DialogTitle>
            <DialogDescription>
              {editingExpense?.status === "rejected" && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  <strong>Reddetme Gerekçesi:</strong> {editingExpense.rejection_reason || "Belirtilmemiş"}
                </div>
              )}
              {!editingExpense && "Denetim masrafınızı kaydedin"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="audit_id">Denetim *</Label>
              <Select
                value={formData.audit_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, audit_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Denetim seçin" />
                </SelectTrigger>
                <SelectContent>
                  {audits.filter(a => isAuditor ? a.assigned_auditor_id === user?.user_id : true).map(audit => (
                    <SelectItem key={audit.audit_id} value={audit.audit_id}>
                      {audit.customer_name} ({new Date(audit.scheduled_date).toLocaleDateString('tr-TR')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expense_type">Masraf Türü *</Label>
              <Select
                value={formData.expense_type || undefined}
                onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tür seçin" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount">Tutar (TRY) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expense_date">Tarih *</Label>
              <Input
                id="expense_date"
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                placeholder="Masraf detaylarını yazın..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90">
                {editingExpense?.status === "rejected" ? "Güncelle ve Gönder" : "Kaydet"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Rejection Reason Dialog */}
      <RejectDialog 
        open={showRejectDialog} 
        onClose={() => setShowRejectDialog(false)} 
        onConfirm={confirmReject}
      />

      {/* Receipt Viewer Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Fişler / Faturalar</DialogTitle>
            <DialogDescription>
              {selectedExpense?.auditor_name} - {selectedExpense && formatDate(selectedExpense.expense_date)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedExpense?.receipt_urls?.map((url, index) => (
              <div key={index} className="border rounded-lg p-2">
                <ReceiptImage 
                  url={`${BACKEND_URL}${url}`} 
                  alt={`Fiş ${index + 1}`}
                  index={index}
                />
                <div className="mt-2 text-center">
                  <a
                    href={`${BACKEND_URL}${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Tam Boyut Görüntüle
                  </a>
                </div>
              </div>
            ))}
            
            {isAuditor && selectedExpense?.status === "pending" && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <div className="flex flex-col items-center gap-2">
                    {uploading ? (
                      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    ) : (
                      <Upload className="w-8 h-8 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-500">
                      {uploading ? "Yükleniyor..." : "Yeni fiş eklemek için tıklayın"}
                    </span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
